import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import Select from "react-select";
import { AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Boxes, Package, Save, WalletCards } from "lucide-react";
import { numberFormat } from "../../config/Config";

const EntradaEstoque = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const userId = user?.id;

  const [movimentoData, setMovimentoData] = useState({
    tipo_movimento: "Entrada",
    produto_id: "",
    quantidade: "",
    descricao: "",
  });
  const [produtos, setProdutos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);

  const optionsTipo = [
    { value: "Entrada", label: "Entrada de Stock" },
    { value: "Saída", label: "Saida de Stock / Ajuste" },
  ];

  const selectedProduto = useMemo(
    () => produtos.find((produto) => produto.value === movimentoData.produto_id)?.raw,
    [produtos, movimentoData.produto_id]
  );

  const fetchProdutos = async () => {
    const response = await api.get(`/app/produtos/filter/?empresa_id=${empresaId}&tipo=Produto`);
    setProdutos(response.data.map((produto) => ({
      value: produto.id,
      label: `${produto.nome} (${produto.quantidade || 0} ${produto.unidade || "un"})`,
      raw: produto,
    })));
  };

  const fetchResumo = async () => {
    const response = await api.get(`/app/movimentos-estoque/empresa/${empresaId}/resumo/`);
    setResumo(response.data);
  };

  const refresh = async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      await Promise.all([fetchProdutos(), fetchResumo()]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [empresaId]);

  const handleSaveMovimento = async (event) => {
    event.preventDefault();
    if (!movimentoData.produto_id) return Swal.fire("Atencao", "Selecione um produto.", "warning");
    if (!movimentoData.quantidade || Number(movimentoData.quantidade) <= 0) {
      return Swal.fire("Atencao", "Informe uma quantidade valida.", "warning");
    }

    try {
      await api.post("/app/movimentos-estoque/", {
        ...movimentoData,
        empresa_id: empresaId,
        user_id: userId,
      });

      Swal.fire("Sucesso", "Movimento registado e stock atualizado.", "success");
      setMovimentoData((current) => ({
        tipo_movimento: current.tipo_movimento,
        produto_id: "",
        quantidade: "",
        descricao: "",
      }));
      await refresh();
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Nao foi possivel registar o movimento.", "error");
    }
  };

  const selectStyle = {
    control: (base) => ({
      ...base,
      borderRadius: 0,
      minHeight: "36px",
      fontSize: "13px",
      borderColor: "#8a8886",
      boxShadow: "none",
      "&:hover": { borderColor: "#0078d4" },
    }),
    menu: (base) => ({ ...base, borderRadius: 0, fontSize: "13px" }),
  };

  const metrics = resumo?.metrics || {};
  const movementIcon = movimentoData.tipo_movimento === "Entrada" ? <ArrowDownToLine size={18} /> : <ArrowUpFromLine size={18} />;

  return (
    <div id="layout-wrapper">
      <Navbar />
      <Sidebar />

      <main className="app-wrapper">
        <div className="stock-shell">
          <header className="stock-titlebar">
            <div>
              <span>Armazem</span>
              <h1>Gestao de estoque</h1>
            </div>
            <button className="win10-btn win10-btn-outline" type="button" onClick={refresh} disabled={loading}>
              Atualizar
            </button>
          </header>

          <section className="stock-metrics">
            <div className="metric-tile">
              <Boxes size={20} />
              <span>Produtos</span>
              <strong>{metrics.totalProdutos || 0}</strong>
            </div>
            <div className="metric-tile">
              <Package size={20} />
              <span>Unidades</span>
              <strong>{numberFormat(metrics.totalUnidades || 0, 2)}</strong>
            </div>
            <div className="metric-tile">
              <WalletCards size={20} />
              <span>Total a vender</span>
              <strong>{numberFormat(metrics.totalVendaEsperado || 0, 2)} Kz</strong>
            </div>
            <div className="metric-tile danger">
              <AlertTriangle size={20} />
              <span>Baixo stock</span>
              <strong>{metrics.baixoStock || 0}</strong>
            </div>
          </section>

          <section className="stock-grid">
            <form className="stock-panel" onSubmit={handleSaveMovimento}>
              <div className="panel-header">
                {movementIcon}
                <strong>Novo movimento</strong>
              </div>

              <label>Tipo de movimento</label>
              <Select
                styles={selectStyle}
                options={optionsTipo}
                value={optionsTipo.find((option) => option.value === movimentoData.tipo_movimento)}
                onChange={(selected) => setMovimentoData({ ...movimentoData, tipo_movimento: selected.value })}
              />

              <label>Produto</label>
              <Select
                styles={selectStyle}
                options={produtos}
                placeholder="Pesquisar produto..."
                value={produtos.find((produto) => produto.value === movimentoData.produto_id) || null}
                onChange={(selected) => setMovimentoData({ ...movimentoData, produto_id: selected ? selected.value : "" })}
              />

              {selectedProduto && (
                <div className="stock-preview">
                  <span>Stock atual</span>
                  <strong>{selectedProduto.quantidade || 0} {selectedProduto.unidade || "un"}</strong>
                </div>
              )}

              <label>Quantidade</label>
              <input
                type="number"
                step="0.01"
                required
                value={movimentoData.quantidade}
                onChange={(event) => setMovimentoData({ ...movimentoData, quantidade: event.target.value })}
              />

              <label>Observacoes / justificacao</label>
              <textarea
                rows={3}
                placeholder="Ex: Compra ao fornecedor, ajuste de inventario, quebra..."
                value={movimentoData.descricao}
                onChange={(event) => setMovimentoData({ ...movimentoData, descricao: event.target.value })}
              />

              <button className="win10-btn win10-btn-primary" type="submit">
                <Save size={16} /> Guardar movimento
              </button>
            </form>

            <div className="stock-panel">
              <div className="panel-header">
                <AlertTriangle size={18} />
                <strong>Produtos em alerta</strong>
              </div>
              <div className="compact-list">
                {(resumo?.baixoStock || []).slice(0, 8).map((produto) => (
                  <div className="compact-row" key={produto.id}>
                    <span>{produto.nome}</span>
                    <strong>{produto.quantidade} {produto.unidade}</strong>
                  </div>
                ))}
                {(!resumo?.baixoStock || resumo.baixoStock.length === 0) && (
                  <div className="empty-state">Sem alertas de stock.</div>
                )}
              </div>
            </div>
          </section>

          <section className="stock-panel">
            <div className="panel-header">
              <Package size={18} />
              <strong>Ultimos movimentos</strong>
            </div>
            <div className="movement-table">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Produto</th>
                    <th>Tipo</th>
                    <th>Qtd</th>
                    <th>Operador</th>
                    <th>Obs.</th>
                  </tr>
                </thead>
                <tbody>
                  {(resumo?.movimentos || []).map((movimento) => (
                    <tr key={movimento.id}>
                      <td>{new Date(movimento.createdAt).toLocaleString("pt-AO")}</td>
                      <td>{movimento.Produto?.nome || "-"}</td>
                      <td><span className={movimento.tipo_movimento === "Entrada" ? "pill in" : "pill out"}>{movimento.tipo_movimento}</span></td>
                      <td>{movimento.quantidade}</td>
                      <td>{movimento.User?.nome || "-"}</td>
                      <td>{movimento.descricao || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      <style>{`
        .stock-shell { display: grid; gap: 14px; }
        .stock-titlebar { min-height: 72px; border: 1px solid #d2d0ce; background: #fff; display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; }
        .stock-titlebar span { font-size: 11px; font-weight: 800; color: #605e5c; text-transform: uppercase; }
        .stock-titlebar h1 { margin: 2px 0 0; font-size: 24px; font-weight: 800; color: #201f1e; }
        .stock-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
        .metric-tile { border: 1px solid #d2d0ce; background: #fff; padding: 14px; display: grid; gap: 4px; color: #323130; }
        .metric-tile svg { color: #0078d4; }
        .metric-tile span { font-size: 11px; font-weight: 800; color: #605e5c; text-transform: uppercase; }
        .metric-tile strong { font-size: 22px; font-weight: 800; }
        .metric-tile.danger svg, .metric-tile.danger strong { color: #d13438; }
        .stock-grid { display: grid; grid-template-columns: minmax(320px, 460px) 1fr; gap: 14px; }
        .stock-panel { border: 1px solid #d2d0ce; background: #fff; padding: 14px; display: grid; gap: 10px; }
        .panel-header { height: 34px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #edebe9; color: #201f1e; }
        .stock-panel label { font-size: 11px; font-weight: 800; color: #605e5c; text-transform: uppercase; }
        .stock-panel input, .stock-panel textarea { width: 100%; border: 1px solid #8a8886; border-radius: 0; padding: 8px 10px; font-size: 13px; outline: 0; }
        .stock-panel input:focus, .stock-panel textarea:focus { border-color: #0078d4; outline: 1px solid #0078d4; }
        .stock-preview { display: flex; justify-content: space-between; align-items: center; background: #f3f2f1; border: 1px solid #edebe9; padding: 10px; }
        .stock-preview span { color: #605e5c; font-size: 12px; }
        .compact-list { display: grid; gap: 6px; }
        .compact-row { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #edebe9; padding: 8px 0; }
        .compact-row strong { color: #d13438; }
        .empty-state { color: #605e5c; padding: 20px 0; text-align: center; }
        .movement-table { overflow: auto; }
        .movement-table table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .movement-table th { text-align: left; background: #f3f2f1; border-bottom: 1px solid #d2d0ce; padding: 9px; font-size: 11px; text-transform: uppercase; }
        .movement-table td { border-bottom: 1px solid #edebe9; padding: 8px 9px; }
        .pill { display: inline-flex; align-items: center; min-height: 22px; padding: 0 8px; font-weight: 800; font-size: 11px; }
        .pill.in { background: #e6f4ea; color: #107c10; }
        .pill.out { background: #fde7e9; color: #a4262c; }
        .win10-btn { border-radius: 0; border: 1px solid transparent; min-height: 34px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-weight: 700; }
        .win10-btn-primary { background: #0078d4; color: #fff; }
        .win10-btn-outline { background: #fff; color: #323130; border-color: #8a8886; }
        @media (max-width: 1100px) { .stock-metrics { grid-template-columns: repeat(2, 1fr); } .stock-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default EntradaEstoque;
